import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermissions?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredPermissions = [] 
}) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary-50">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600 mx-auto" />
          <p className="mt-2 text-sm text-secondary-600">A carregar...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to login page with return url
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check permissions if required
  if (requiredPermissions.length > 0 && user) {
    const hasPermission = requiredPermissions.some(permission => {
      switch (permission) {
        case 'admin':
          return user.tipo === 'admin_global';
        case 'manager':
          return user.tipo === 'admin_global' || user.tipo === 'manager_restaurante';
        case 'staff':
          return ['admin_global', 'manager_restaurante', 'staff'].includes(user.tipo);
        default:
          return false;
      }
    });

    if (!hasPermission) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-secondary-50">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-secondary-900 mb-2">
              Acesso Negado
            </h2>
            <p className="text-secondary-600">
              Não tem permissões para aceder a esta página.
            </p>
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
